import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { UserMenu } from '../components/UserMenu';
import { CreditCard, Package, RefreshCw, AlertCircle } from 'lucide-react';
import { BackgroundPattern } from '../components/ui/background-pattern';

interface CreditPackage {
  id: number;
  name: string;
  credits: number;
  price: number;
  description: string;
}

// Subscription interface removed as it's coming soon

// Component for the copy button with visual feedback
const CopyButton: React.FC<{ email: string }> = ({ email }) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  
  const handleCopy = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Reset copied state after 1.5 seconds
    timeoutRef.current = setTimeout(() => {
      setCopied(false);
    }, 1500);
  };
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return (
    <button 
      onClick={handleCopy}
      className={`inline-flex items-center p-2 ${copied ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200'} font-medium rounded-md transition-colors`}
      title="Copy email address to clipboard"
      aria-label="Copy email address to clipboard"
    >
      {copied ? (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      )}
    </button>
  );
};

const Billing: React.FC = () => {
  const { user, refreshUserData, api } = useAuth();
  const [creditPackages, setCreditPackages] = useState<CreditPackage[]>([]);
  // Subscriptions functionality removed as it's coming soon
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use useCallback to memoize the fetchData function
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch credit packages
      const packagesResponse = await api.get('/api/credits/packages');
      setCreditPackages(packagesResponse.data.packages);

      // Subscriptions functionality removed as it's coming soon
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError('Failed to load billing information. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [api]);
  
  // Single useEffect for fetching all data on component mount
  useEffect(() => {
    fetchData();
    // We don't call refreshUserData() here to avoid infinite API calls
    // The user data should already be available from the AuthContext
  }, [fetchData]);

  const handlePurchaseCredits = async (packageId: number) => {
    try {
      const response = await api.post('/api/stripe/create-checkout-session', {
        package_id: packageId
      });
      
      if (response.data.success && response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        setError('Failed to create checkout session. Please try again.');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to process your request. Please try again later.');
    }
  };

  // Subscription management functionality removed as it's coming soon

  return (
    <div className="relative min-h-screen">
      <BackgroundPattern />
      <div className="relative">
        {/* Custom header */}
        <header className="fixed top-0 left-0 right-0 bg-transparent backdrop-blur-sm border-b border-gray-200/10 dark:border-gray-800/10 z-50">
          <div className="container max-w-screen-2xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-8">
              <Link 
                to="/dashboard" 
                className="flex items-center text-xl font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
              >
                <img src="/i-can-pitch-logo.jpg" alt="Logo" className="h-8 w-auto mr-2" />
                ICanPitch
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <UserMenu showLogout={true} />
            </div>
          </div>
        </header>
        
        <main className="min-h-screen bg-transparent">
          <div className="container mx-auto py-8 px-4 pt-24 pb-12">
            <h1 className="text-5xl font-extrabold mb-6 tracking-tight text-center">
              <span className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-400 bg-clip-text text-transparent">
                Billing & Subscriptions
              </span>
            </h1>
            
            <div className="flex flex-col items-center">
              <div className="w-full max-w-3xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="text-red-500 mr-2" size={20} />
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Current Credits */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6 mb-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Current Balance</h2>
                  <div className="flex items-baseline">
                    {/* Console log outside of JSX */}
                    <span className="text-4xl font-extrabold text-indigo-600 dark:text-indigo-400">{(() => {
                      console.log('Rendering credits in Billing:', user?.credits);
                      return user?.credits || 0;
                    })()}</span>
                    <span className="ml-2 text-xl text-gray-500 dark:text-gray-400">credits</span>
                  </div>
                </div>
                
                <button 
                  onClick={async () => {
                    try {
                      await refreshUserData();
                      await fetchData();
                    } catch (err) {
                      console.error('Error refreshing data:', err);
                    }
                  }} 
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors shadow-sm self-start md:self-center"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Refresh
                </button>
              </div>
            </div>

            {/* Subscriptions Coming Soon Message */}
            <div className="bg-gradient-to-br from-white to-indigo-50 dark:from-gray-800 dark:to-indigo-900/20 rounded-lg shadow-md border border-indigo-100 dark:border-indigo-900/30 p-8 transition-all hover:shadow-lg">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex-1 space-y-4">
                  <div className="flex items-center">
                    <div className="bg-indigo-100 dark:bg-indigo-900/40 p-3 rounded-full mr-4">
                      <CreditCard className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Premium Subscriptions</h3>
                  </div>
                  
                  <div className="pl-2 border-l-4 border-indigo-500 dark:border-indigo-400">
                    <p className="text-gray-700 dark:text-gray-300 text-lg">
                      We're finalizing our subscription plans to give you the best experience possible.
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">
                      Want early access? Contact us for premium credits and be among the first to experience all features.
                    </p>
                  </div>
                  
                </div>
                
                {/* Centered Contact Buttons */}
                <div className="flex justify-center items-center space-x-3 pt-4">
                  <a 
                    href="mailto:neeta@icanpitch.com" 
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors shadow-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    Contact Us
                  </a>
                  <CopyButton email="neeta@icanpitch.com" />
                </div>
                
                <div className="flex-1 space-y-4 hidden">
                  {/* This is an empty div to maintain the flex layout */}
                </div>
                
                <div className="hidden md:block md:flex-1">
                  <div className="relative">
                    <div className="absolute -top-6 -right-6 bg-indigo-500 text-white text-xs font-bold px-3 py-1 rounded-full transform rotate-12">
                      Coming Soon
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-inner border border-gray-200 dark:border-gray-700">
                      <div className="flex flex-col items-center justify-center space-y-2 opacity-75">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <div className="text-center">
                          <div className="font-semibold text-gray-700 dark:text-gray-300">Premium Plan</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">Unlimited access</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Billing;
